import React, { useEffect, useState } from "react";
import ApiDataBase from "server";
import UseContext from "hooks/useContext";
import SectionWrapper, {
    FormField,
} from "pages/createMission/components/SectionWrapper";
import StyledInput from "components/styledInput/StyledInput";
import Tooltip from "components/tooltips/Tooltips";
import { InformationIcon } from "assets/icons";
import MultiDdl from "components/componentsForm/multiDdl";
import { compareLanguages, languageGroup } from "components/componentsForm/languageGroup";
import TemplateToolList from "pages/missionTemplates/components/TemplateToolList";
import {
    DEFAULT_TOOL_PROVIDER,
    normalizeTemplateTools,
    toolNameKey,
} from "pages/missionTemplates/utils/templateTools";
import { DRESS_CODE_MAX_LENGTH } from "pages/missionTemplates/utils/templateDressCode";

// Tooltip only listens to the mouse: focus replays the same events so the
// hint also opens from the keyboard.
const showTooltipOnFocus = (e) =>
    e.currentTarget.dispatchEvent(new MouseEvent("mouseover", { bubbles: true }));
const hideTooltipOnBlur = (e) =>
    e.currentTarget.dispatchEvent(new MouseEvent("mouseout", { bubbles: true }));

const DressCodeInfo = ({ text }) => (
    <Tooltip text={text} placement="top">
        <button
            type="button"
            aria-label={text}
            onFocus={showTooltipOnFocus}
            onBlur={hideTooltipOnBlur}
            className="flex items-center rounded cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
        >
            <InformationIcon wh={16} color={"#6B7280"} />
        </button>
    </Tooltip>
);

const PrerequisTemplate = () => {
    const { templateData, setTemplateData, language } = UseContext();
    const [toolReferential, setToolReferential] = useState([]);
    const [licences, setLicences] = useState([]);
    const [languages, setLanguages] = useState([]);

    useEffect(() => {
        ApiDataBase.getAllLicence(
            (res) => setLicences(res),
            (err) => console.warn(err)
        );
        ApiDataBase.getListTools(
            (res) => setToolReferential(res),
            (err) => console.warn(err)
        );
        ApiDataBase.getListLanguages(
            (res) => setLanguages(res),
            (err) => console.warn(err)
        );
    }, []);

    // The rows are the source of truth: a tool missing from the referential
    // (renamed or removed since) must stay visible and removable.
    const selectedTools = templateData.tools || [];
    const selectedToolKeys = new Set(
        selectedTools.map((tool) => toolNameKey(tool.name))
    );
    const referentialTools = normalizeTemplateTools(
        Array.isArray(toolReferential)
            ? toolReferential.map((tool) => tool?.name)
            : []
    );
    const referentialToolKeys = new Set(
        referentialTools.map((tool) => toolNameKey(tool.name))
    );

    const updateTools = (update) =>
        setTemplateData((prev) => ({
            ...prev,
            tools: update(prev.tools || []),
        }));
    // The menu ticks and unticks tools: a ticked tool is added with the default provider, an
    // unticked one removed, the others keep their provider. A tool outside the referential is
    // not in the menu and stays (removable from its line).
    const handleToolsPicked = (names) => {
        const pickedKeys = new Set(names.map(toolNameKey));
        updateTools((tools) => {
            const kept = tools.filter(
                (tool) =>
                    pickedKeys.has(toolNameKey(tool.name)) ||
                    !referentialToolKeys.has(toolNameKey(tool.name))
            );
            const keptKeys = new Set(kept.map((tool) => toolNameKey(tool.name)));
            const added = names
                .filter((name) => !keptKeys.has(toolNameKey(name)))
                .map((name) => ({ name, providedBy: DEFAULT_TOOL_PROVIDER }));
            return [...kept, ...added];
        });
    };
    const handleToolProviderChange = (name, providedBy) =>
        updateTools((tools) =>
            tools.map((tool) =>
                tool.name === name ? { ...tool, providedBy } : tool
            )
        );
    const handleRemoveTool = (name) =>
        updateTools((tools) => tools.filter((tool) => tool.name !== name));
    const handleLicences = (selected) =>
        setTemplateData((prev) => ({ ...prev, licences: selected }));
    const handleLanguages = (selected) =>
        setTemplateData((prev) => ({ ...prev, languages: selected }));
    const dressCodeLabel = language.getValue(
        "missionTemplate.prerequis.dressCode",
        "Dress code"
    );

    const dressCodeTooltip = language.getValue(
        "missionTemplate.prerequis.dressCodeTooltip",
        "Style vestimentaire attendu pendant la mission"
    );

    const handleDressCode = (e) =>
        setTemplateData((prev) => ({ ...prev, dressCode: e.target.value }));

    return (
        <SectionWrapper
            title={language.getValue(
                "missionTemplate.prerequis.title",
                "Pré-requis"
            )}
            description={language.getValue(
                "missionTemplate.prerequis.description",
                "Mentionnez les qualifications et matériels essentiels pour ces missions."
            )}
        >
            <FormField
                label={language.getValue(
                    "missionTemplate.prerequis.tools",
                    "Matériel"
                )}
            >
                <MultiDdl
                    data={referentialTools}
                    selectArray={handleToolsPicked}
                    defaultSelect={referentialTools
                        .filter((tool) => selectedToolKeys.has(toolNameKey(tool.name)))
                        .map((tool) => tool.name)}
                    showValues={false}
                    placeholder={language.getValue(
                        "missionTemplate.prerequis.toolsPlaceholder",
                        "Ajouter du matériel"
                    )}
                    noOptionsMessage={language.getValue(
                        "missionTemplate.tools.noOptions",
                        "Aucun matériel disponible"
                    )}
                />
                <TemplateToolList
                    tools={selectedTools}
                    onProviderChange={handleToolProviderChange}
                    onRemove={handleRemoveTool}
                />
            </FormField>
            <FormField
                label={language.getValue(
                    "missionTemplate.prerequis.licences",
                    "Permis"
                )}
            >
                <MultiDdl
                    data={licences}
                    selectArray={handleLicences}
                    defaultSelect={templateData.licences || []}
                    placeholder={language.getValue(
                        "missionTemplate.prerequis.licencesPlaceholder",
                        "Ajouter un permis"
                    )}
                />
            </FormField>
            <FormField
                label={language.getValue(
                    "missionTemplate.prerequis.languages",
                    "Langues"
                )}
            >
                <MultiDdl
                    data={languages}
                    selectArray={handleLanguages}
                    groupOf={languageGroup}
                    compare={compareLanguages}
                    defaultSelect={templateData.languages || []}
                    placeholder={language.getValue(
                        "missionTemplate.prerequis.languagesPlaceholder",
                        "Ajouter une langue"
                    )}
                />
            </FormField>
            <FormField
                label={dressCodeLabel}
                aside={<DressCodeInfo text={dressCodeTooltip} />}
            >
                <StyledInput
                    type={"textarea"}
                    value={templateData.dressCode || ""}
                    maxLength={DRESS_CODE_MAX_LENGTH}
                    placeholder={language.getValue(
                        "missionTemplate.prerequis.dressCodePlaceholder",
                        "Ex : polo noir et pantalon noir (vêtements en couleur non acceptés)"
                    )}
                    onChange={handleDressCode}
                />
            </FormField>
        </SectionWrapper>
    );
};

export default PrerequisTemplate;
